import { useCallback, useState } from 'react'
import type { CreateLayoutPayload, Layout, LayoutDetail, UpdateLayoutPayload } from 'types/layouts'
import type { MutationResult, QueryResult } from 'utils/api'
import { qs } from 'utils/api'

import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

export interface LayoutsParams {
  skip?: number
  take?: number
  active?: boolean
  includeInactive?: boolean
  q?: string
}

const unwrapLayouts = (response: { data: Layout[] }) => response.data

export function useLayouts(params?: LayoutsParams): QueryResult<Layout[]> {
  return useApiQuery(`/api/layouts${qs(params)}`, { select: unwrapLayouts })
}

export function useLayout(id: string | undefined): QueryResult<LayoutDetail> {
  return useApiQuery<LayoutDetail>(id ? `/api/layouts/${id}` : null)
}

export function useCreateLayout(): MutationResult<Layout, [CreateLayoutPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (payload: CreateLayoutPayload): Promise<Layout> => {
      setLoading(true)
      try {
        return await fetchApi<Layout>('/api/layouts', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useUpdateLayout(): MutationResult<Layout, [string, UpdateLayoutPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string, payload: UpdateLayoutPayload): Promise<Layout> => {
      setLoading(true)
      try {
        return await fetchApi<Layout>(`/api/layouts/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useDeleteLayout(): MutationResult<Layout, [string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string): Promise<Layout> => {
      setLoading(true)
      try {
        return await fetchApi<Layout>(`/api/layouts/${id}`, { method: 'DELETE' })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}
